import type { ActionKind, BusinessVertical } from "@graftus/schema";

// ---------------------------------------------------------------------------
// Vertical configuration (GRA-167)
//
// One typed config per business vertical: the nav slot, the Översikt KPI
// card set and banner, the Medarbetare variant column and the recommended
// action kinds. Views read this through `useVerticalConfig` instead of
// branching on `isDvhBusiness`/`vertical === "dvh"` themselves, so adding a
// third vertical (e.g. public sector) means adding one record here, not
// hunting down every call site.
// ---------------------------------------------------------------------------

export type NavGoalsFeature = "performance" | "goals";

export interface VerticalNavConfig {
  /**
   * Which "Mål" row renders: the KPI/trend page (sales) or the per-department
   * focus-goals page (grocery store, GRA-83). Mutually exclusive — exactly
   * one is visible.
   */
  goalsFeature: NavGoalsFeature;
  /** Whether the "Rutiner" nav row (store routines) shows at all. */
  showRoutines: boolean;
  /** `nav` i18n key for the Underlag report row ("reports" vs "grossProfit"). */
  reportsLabelKey: "reports" | "grossProfit";
  /** The report row's URL for this vertical. */
  reportsUrl: string;
  /**
   * Reports are the main data source for this vertical, so anyone with
   * people access may open the row without an explicit report permission.
   */
  reportsOpenToPeopleAccess: boolean;
  /** The Coach product's first-run wizard: upload a report, or connect apps. */
  firstRunWizard: "store" | "connectApps";
}

export type OverviewBannerType = "none" | "routines";

export interface VerticalOverviewConfig {
  /**
   * Extra banner/tile on Översikt beyond the shared KPI tiles: "routines"
   * for a grocery store's "Butiksrutiner" tile, "none" for sales.
   */
  bannerType: OverviewBannerType;
  /** Whether the deadlines column shows the weekly report-upload card. */
  showReportDeadlineCard: boolean;
  /**
   * Department tiles group by direct children only (a grocery store's
   * departments under its store root) rather than showing every team flat.
   */
  groupTeamTilesByChildren: boolean;
}

export type MedarbetareVariantColumn = "assessment" | "kpi";

export interface VerticalMedarbetareConfig {
  /** The row's vertical-specific column: the test pill, or the member's KPI. */
  variantColumn: MedarbetareVariantColumn;
}

export interface VerticalConfig {
  vertical: BusinessVertical;
  nav: VerticalNavConfig;
  overview: VerticalOverviewConfig;
  medarbetare: VerticalMedarbetareConfig;
  /** Manual "Agera" kinds offered for this vertical (praise/boost/talk/follow-up today). */
  recommendedActionKinds: ActionKind[];
}

const SHARED_ACTION_KINDS: ActionKind[] = ["berom", "boosta", "samtal", "uppfoljning"];

export const VERTICAL_CONFIG: Record<BusinessVertical, VerticalConfig> = {
  sales: {
    vertical: "sales",
    nav: {
      goalsFeature: "performance",
      showRoutines: false,
      reportsLabelKey: "reports",
      reportsUrl: "/reports",
      reportsOpenToPeopleAccess: false,
      firstRunWizard: "connectApps",
    },
    overview: {
      bannerType: "none",
      showReportDeadlineCard: false,
      groupTeamTilesByChildren: false,
    },
    medarbetare: { variantColumn: "kpi" },
    recommendedActionKinds: SHARED_ACTION_KINDS,
  },
  dvh: {
    vertical: "dvh",
    nav: {
      goalsFeature: "goals",
      showRoutines: true,
      reportsLabelKey: "grossProfit",
      reportsUrl: "/bruttovinst",
      reportsOpenToPeopleAccess: true,
      firstRunWizard: "store",
    },
    overview: {
      bannerType: "routines",
      showReportDeadlineCard: true,
      groupTeamTilesByChildren: true,
    },
    medarbetare: { variantColumn: "assessment" },
    recommendedActionKinds: SHARED_ACTION_KINDS,
  },
};

export function getVerticalConfig(vertical: BusinessVertical): VerticalConfig {
  return VERTICAL_CONFIG[vertical];
}
